import { alpha, Theme } from '@mui/material/styles';
import bnplCardBackground from '../../assets/background_forms_dark_secondary.svg';

export const BNPL_CARD_RADIUS = 2.5;

const cardGlowAfter = (theme: Theme) => ({
  content: '""',
  position: 'absolute' as const,
  right: '-18%',
  top: '10%',
  width: '52%',
  paddingBottom: '52%',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(theme.palette.white.main, 0.14)} 0%, transparent 72%)`,
  pointerEvents: 'none' as const,
});

export function getBnplPurchasingPowerStyles(theme: Theme) {
  return {
    root: {
      width: '100%',
      borderRadius: BNPL_CARD_RADIUS,
    },
    emptyStateContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: '',
      justifyContent: 'center',
      gap: 2,
      padding: 5,
      // backgroundColor: theme.palette.background.default,
      borderRadius: BNPL_CARD_RADIUS,
      border: `1px solid ${alpha(theme.palette.black.main, 0.05)}`,
      boxShadow: `2px 2px 5px ${alpha(theme.palette.black.main, 0.05)}`,
    },
    cardSkeleton: {
      borderRadius: BNPL_CARD_RADIUS,
      backgroundImage: `url(${bnplCardBackground}), linear-gradient(115deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative' as const,
      overflow: 'hidden' as const,
      paddingTop: { xs: 5, md: 10 },
      paddingInline: { xs: 5, md: 10 },
      paddingBottom: 0,
      minHeight: 125,
      width: '100%',
      color: theme.palette.white.main,
      '&::after': cardGlowAfter(theme),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      boxShadow: `2px 2px 5px ${alpha(theme.palette.primary.main, 0.3)}`,
      zIndex: 2,
    },
    card: {
      overflow: 'hidden' as const,
      paddingTop: { xs: 5, md: 10 },
      paddingInline: { xs: 5, md: 10 },
      paddingBottom: 0,
      minHeight: 125,
      width: '100%',
      color: theme.palette.white.main,
      '&::after': cardGlowAfter(theme),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      boxShadow: `2px 2px 5px ${alpha(theme.palette.primary.main, 0.3)}`,
      zIndex: 2,
    },
    cardInner: {
      position: 'relative' as const,
      zIndex: 1,
    },
    headerRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1,
      mb: 1,
    },
    labelRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minWidth: 0,
    },
    labelCaption: {
      fontWeight: 600,
      letterSpacing: 0.6,

      textTransform: 'uppercase' as const,
      color: theme.palette.grey[300],
    },
    bagIcon: {
      fontSize: 20,
      opacity: 0.95,
      color: theme.palette.grey[300],
    },
    infoIconButton: {
      p: 0.25,
      mt: -0.25,
    },
    infoIcon: {
      fontSize: 20,
      color: theme.palette.grey[300],
    },
    infoPanelCollapse: {
      width: '100%',
    },
    infoPanelInner: {
      pb: 1.5,
      pr: { xs: 0, sm: 1 },
    },
    infoPanelCaption: {
      color: alpha(theme.palette.white.main, 0.88),
      lineHeight: 1.5,
      display: 'block',
    },
    amountTypography: {
      fontSize: { xs: '2.25rem', sm: '2.75rem' },
      fontFamily: 'Aeonik',
      letterSpacing: 0.2,
      color: theme.palette.white.main,
      mt: 0.5,
    },
    skeletonRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 2,
    },
    skeletonInnerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    },
    skeletonShimmer: {
      bgcolor: alpha(theme.palette.white.main, 0.25),
    },
    skeletonShimmerStrong: {
      bgcolor: alpha(theme.palette.white.main, 0.3),
    },
    movementsButton: {
      mt: 1.5,
      p: 0,
      width: 'max-content',
      justifyContent: 'flex-start',
      textAlign: 'left',
      fontWeight: 600,
      textDecoration: 'underline',
      color: theme.palette.secondary.main,
    },
    emptyStateTypography: {
      py: 1,
      color: theme.palette.white.main,
    },
    gradient1: {
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(100deg, ${alpha(theme.palette.primary.main, 0.7)} 0%, ${alpha(theme.palette.secondary.main, 0.8)} 33%, ${alpha(theme.palette.primary.main, 0.5)} 70%, ${alpha(theme.palette.tertiary.main, 0.5)} 100%)`,
    },
    gradient2: {
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(100deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.8)} 33%, ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
    },
    heroImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center 37%',
    },
  };
}

export const bnplMovementsPanelSx = {
  mt: 2,
  p: 2,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
};

export function getBnplNextMonthSummaryCardStyles(theme: Theme) {
  return {
    root: {
      width: '100%',
    },
    card: {
      borderRadius: BNPL_CARD_RADIUS,
      bgcolor: theme.palette.white.main,
      border: `1px solid ${alpha(theme.palette.black.main, 0.1)}`,
      boxShadow: `1px 1px 5px ${alpha(theme.palette.black.main, 0.15)}`,
      padding: { xs: 5, md: 10 },
      minHeight: 100,
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1.5,
    },
    headerIconWrap: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
    },
    headerIcon: {
      fontSize: 20,
      color: theme.palette.grey[500],
    },
    label: {
      letterSpacing: 0.5,
      textTransform: 'uppercase' as const,
      color: theme.palette.grey[600],
    },
    headline: {
      fontSize: { xs: '2rem', sm: '2.25rem' },
      fontFamily: 'Aeonik',
      letterSpacing: 0.2,
      mb: 1,
    },
    helperText: {
      color: theme.palette.grey[600],
    },
    progress: {
      height: 8,
      borderRadius: 4,
      bgcolor: theme.palette.grey[300],
      '& .MuiLinearProgress-bar': {
        borderRadius: 4,
        bgcolor: theme.palette.secondary.main,
      },
    },
  };
}

const MOVEMENT_AMOUNT_GREEN = '#15803d';

export function getBnplMovementsStyles(theme: Theme) {
  return {
    buttonDialog: {
      display: 'flex',
      paddingInline: { xs: 2.5, md: 7.5 },
      marginBlock: 2.5,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    panelHeaderRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      mb: 2.5,
    },
    panelContent: {
      marginTop: 1,
      minWidth: { xs: '90vw', md: '450px' },
      paddingLeft: 5,
      paddingRight: 7.5,
      paddingBlock: 1,
      overflowY: 'auto',
      maxHeight: { xs: '65vh', md: '500px' },
      bgcolor: theme.palette.grey[50],
      borderRadius: BNPL_CARD_RADIUS,
    },
    panelHeaderIcon: {
      color: theme.palette.secondary.main,
      fontSize: 24,
    },
    panelTitle: {
      color: theme.palette.primary.main,
      fontSize: '1.25rem !important',
    },
    accordion: {
      borderRadius: BNPL_CARD_RADIUS,
      overflow: 'hidden',
      bgcolor: theme.palette.white.main,
      border: `1px solid ${alpha(theme.palette.black.main, 0.1)}`,
      boxShadow: `1px 1px 5px ${alpha(theme.palette.black.main, 0.15)}`,
      padding: 2.5,
    },
    accordionSummary: {
      width: '100%',
      '& .MuiAccordionSummary-content': {
        my: 1,
        alignItems: 'center',
        gap: 1.25,
        justifyContent: 'start',
        marginInline: 0,
      },
      '& .MuiAccordionSummary-content.Mui-expanded': {
        marginInline: 0,
      },
    },
    accordionSummaryIcon: {
      color: theme.palette.secondary.main,
      fontSize: 24,
    },
    accordionTitle: {
      color: theme.palette.primary.main,
    },
    accordionDetails: {
      paddingInline: 2.5,
      paddingBottom: 2,
      paddingTop: 0,
      overflowY: 'auto',
      maxHeight: '400px',
      '&::-webkit-scrollbar': {
        width: 2,
      },
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    },
    row: {
      py: 2,
      borderBottom: `1px solid ${alpha(theme.palette.black.main, 0.08)}`,
      '&:last-of-type': { borderBottom: 'none' },
    },
    merchantRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      mb: 1.25,
    },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      flexShrink: 0,
    },
    avatarMuted: {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
    },
    avatarTint: {
      bgcolor: alpha(theme.palette.secondary.main, 0.18),
      color: theme.palette.secondary.main,
    },
    merchantName: {
      fontWeight: 700,
      fontSize: '0.85rem',
      color: theme.palette.primary.main,
      lineHeight: 1.25,
      letterSpacing: 0.02,
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 2,
      ml: 2.75,
      borderLeft: `2px solid ${alpha(theme.palette.black.main, 0.12)}`,
      paddingLeft: 1.5,
    },
    detailLeft: {
      minWidth: 0,
    },
    datailNonFinancial: {
      display: 'block',
      marginBlock: 0.5,
    },
    movementSubTypeCaption: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      display: 'block',
      mb: 0.25,
    },
    dateShort: {
      color: alpha(theme.palette.text.secondary, 0.85),
      display: 'block',
      marginTop: 2.5,
    },
    detailRight: {
      textAlign: 'right',
    },
    amountMovement: {
      fontWeight: 700,
      color: MOVEMENT_AMOUNT_GREEN,
      display: 'block',
      lineHeight: 1,
    },
    amountMovementCredit: {
      color: theme.palette.secondary.main,
    },
    saldoLine: {
      color: alpha(theme.palette.black.main, 0.45),
      fontSize: 12,
    },
    loadMoreWrap: {
      display: 'flex',
      justifyContent: 'center',
      pt: 2,
    },
    emptyText: {
      color: theme.palette.text.secondary,
      py: 1,
    },
  };
}

// Tokens sin equivalente en el theme (fondos lila muy claros del ticket / chip de conteo).
const COUPON_TICKET_BG = '#F5F3FA';
const COUPON_COUNT_CHIP_BG = '#efeafc';

export function getMisCuponesStyles(theme: Theme) {
  return {
    card: {
      width: '100%',
      borderRadius: BNPL_CARD_RADIUS,
      backgroundColor: theme.palette.white.main,
      border: `1px solid ${theme.palette.grey[300]}`,
      boxShadow: `2px 2px 5px ${alpha(theme.palette.tertiary.main, 0.1)}`,
      padding: { xs: 5, md: 7.5 },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2.5,
    },
    headerIcon: {
      marginBottom: -1,
    },
    countChip: {
      backgroundColor: COUPON_COUNT_CHIP_BG,
      color: theme.palette.primary.main,
      fontWeight: 800,
      borderRadius: 999,
      px: 1.25,
      py: '1px',
      mb: -0.5,
    },
    scrollArea: {
      position: 'relative' as const,
    },
    scrollRow: {
      display: 'flex',
      gap: 1.5,
      overflowX: 'auto' as const,
      pr: 2,
      scrollSnapType: 'x proximity',
      '& > *': { scrollSnapAlign: 'start' },
      scrollbarWidth: 'thin' as const,
      scrollbarColor: `${alpha(theme.palette.black.main, 0.2)} transparent`,
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: alpha(theme.palette.black.main, 0.2),
        borderRadius: 4,
      },
    },
    fadeRight: {
      position: 'absolute' as const,
      top: 0,
      right: 0,
      bottom: 8,
      width: 32,
      pointerEvents: 'none' as const,
      background: `linear-gradient(to right, ${alpha(theme.palette.white.main, 0)}, ${theme.palette.white.main})`,
    },
    ticket: {
      display: 'flex',
      flex: '0 0 auto',
      minWidth: 240,
      minHeight: 80,
      marginTop: '1px',
      border: '0.5px solid',
      borderColor: theme.palette.secondary.main,
      borderRadius: 2,
      overflow: 'hidden' as const,
      backgroundColor: COUPON_TICKET_BG,
      '&:hover': { transform: 'translateY(-1px)' },
    },
    ticketIconCol: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 1.5,
      backgroundColor: theme.palette.primary.main,
    },
    ticketIcon: {
      color: theme.palette.tertiary.main,
    },
    ticketBody: {
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center',
      gap: 0.25,
      px: 1.5,
      py: 1,
    },
    ticketMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: 0.25,
      flexWrap: 'wrap' as const,
    },
    ticketCode: {
      backgroundColor: theme.palette.white.main,
      color: theme.palette.primary.main,
      fontSize: 11,
      fontWeight: 800,
      borderRadius: 1,
      border: '1px dashed',
      borderColor: theme.palette.primary.main,
      px: 1,
      py: '1px',
      letterSpacing: 0.5,
    },
  };
}
